import dayjs, { Dayjs } from 'dayjs';
import {
  fetchAvailabilities,
  fetchProfile,
  fetchRebookingAvailabilities,
  fetchServiceTypesForPatient,
  fetchServiceTypesByState,
} from '@api';
import { US_STATES } from '@constants';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib';
import { captureException } from '@sentry/react';
import {
  Availabilities,
  ProviderAvailability,
  RebookingAvailabilities,
  ServiceType,
  UtmParameters,
} from '@types';
import {
  getAvailabilityRangeFromDate,
  removePriceFromServiceTypeName,
} from '@utils';
import { getTimeSlotTime, serviceIsMht } from '@pages/SchedulePage/utils';
import { AvailabilitiesPromise } from './scheduleLoader';
import {
  fetchPatientSubscriptions,
  fetchSubscriptionsAvailableToPatient,
} from 'api/subscriptions';
import { getValue } from '@quickmd-llc/frontend-utils';

export function parseUtmParametersFromSearch(search: string): UtmParameters {
  const searchParams = new URLSearchParams(search);

  const gClid = searchParams.get('gclid');
  const utmCampaign = searchParams.get('utm_campaign');
  const utmContent = searchParams.get('utm_content');
  const utmSource = searchParams.get('utm_source');
  const utmMedium = searchParams.get('utm_medium');
  const utmReferrer = searchParams.get('referrer') || searchParams.get('utm_referrer');
  const utmTerm = searchParams.get('utm_term');
  const utmService = searchParams.get('utm_service');
  const utmReferralCode = searchParams.get('utm_referral_code');
  const fbclid = searchParams.get('fbclid');
  const fbClick = searchParams.get('fbclick');

  const cookies = document.cookie.split('; ').join('&');
  const cookiesParams = new URLSearchParams(cookies);
  const fbpixel = cookiesParams.get('_fbp');
  const fbc = cookiesParams.get('_fbc');
  const gclawArray = cookiesParams.get('_gcl_aw')?.split('.');
  const gclaw = gclawArray ? gclawArray[gclawArray.length - 1] : null;

  return {
    gclid: gClid || gclaw,
    campaign: utmCampaign,
    content: utmContent,
    fbclid: fbc || fbclid || fbClick,
    fbpixel,
    fbp: fbpixel,
    medium: utmMedium,
    queryString: search,
    referrer: utmReferrer,
    source: utmSource,
    term: utmTerm,
    service: utmService,
    referralCode: utmReferralCode,
  };
}

export async function getStateForCurrentLocation(): Promise<
  string | undefined
> {
  try {
    const url = `https://api.ipapi.com/api/check?access_key=${import.meta.env.VITE_IPAPI_API_KEY}`;

    const response = await fetch(url);
    const data = await response.json();

    const dataError = getValue(data, 'error');
    if (dataError) {
      captureException(dataError);
      return;
    }

    const state = US_STATES.find(
      (x) => x.name === getValue(data, 'region_name'),
    )?.abbreviation;

    return state;
  } catch (error) {
    captureException(error);
  }
}

// This is the service type id in production, but not dev/qa environments
const ADDICTION_TREATMENT_SERVICE_TYPE_ID = '3';

async function filterMhtServiceIfNotAvailableToPatient(
  serviceTypes: ServiceType[],
  isAuthed: boolean,
) {
  try {
    const mhtService = serviceTypes.find((serviceType) =>
      serviceIsMht(serviceType.serviceTypeId),
    );
    if (isAuthed && mhtService) {
      const [subscriptionsAvailableToPatient, patientSubscriptions] =
        await Promise.all([
          fetchSubscriptionsAvailableToPatient(),
          fetchPatientSubscriptions(),
        ]);

      const usableCurrentMhtSubscription = patientSubscriptions?.find(
        (subscription) =>
          !subscription.endDateUtc ||
          new Date(subscription.endDateUtc) > new Date(),
      );

      // * NOTE: There is only one subscription currently and it is MHT. We have yet to build out how to handle
      //   identifying different subscriptions, but when we have multiple and the BE has a way to do so, this will need to be updated.
      if (
        !subscriptionsAvailableToPatient.length &&
        !usableCurrentMhtSubscription
      ) {
        return serviceTypes.filter(
          (serviceType) =>
            serviceType.serviceTypeId !== mhtService.serviceTypeId,
        );
      }
    }
    return serviceTypes;
  } catch (error) {
    captureException(error);
    return serviceTypes;
  }
}

export async function loadServiceTypesByState(state: string) {
  try {
    const { setServiceTypes, patient, auth } = useApplicationStore.getState();

    // @TODO: Handle error for invalid state
    const isAuthed = !!(patient.patientProfile && auth?.accessToken);
    const serviceTypes = isAuthed
      ? await fetchServiceTypesForPatient()
      : await fetchServiceTypesByState(state);

    const mhtFilteredServiceTypes =
      await filterMhtServiceIfNotAvailableToPatient(serviceTypes, isAuthed);

    // @NOTE: Filtering for shortDescription
    //        Also temporarily filtering out addiction treatment for some
    //        us states only from onboarding. Will soon be removed from the backend
    //        for these states and then we can remove the client side filter
    const filteredServiceTypes = mhtFilteredServiceTypes
      .filter((serviceType) => {
        if (
          !isAuthed &&
          (state === 'AR' || state === 'WV' || state === 'TN') &&
          serviceType.serviceTypeId === ADDICTION_TREATMENT_SERVICE_TYPE_ID
        ) {
          return false;
        }

        return Boolean(serviceType.shortDescription);
      })
      .map((serviceType) => {
        return {
          ...serviceType,
          name: removePriceFromServiceTypeName(serviceType.name),
        };
      });

    setServiceTypes(filteredServiceTypes);

    return filteredServiceTypes;
  } catch (error) {
    // @TODO: Handle error
    console.error('Error fetching service types:', error);
  }

  return null;
}

export async function refetchAndSetPatientProfile(): Promise<void> {
  const { auth, patient } = useApplicationStore.getState();
  const { clearAuthData } = auth;
  const { setPatientProfile } = patient;

  try {
    const profile = await fetchProfile();
    setPatientProfile(profile);
  } catch {
    clearAuthData();
    throw new Error('Error fetching patient profile');
  }
}

interface AvailabilitiesForStateProps {
  providerId: string | null;
  state: string;
  date: string;
  visitTypeId: string;
  timeZone: string;
  trackAvailability: boolean;
}

// NOTE: Caching because after finding the next date with availability, the schedule page updates the url date param which re-runs the loader
const TTL = 1000;
interface CachedAvailabilities extends Availabilities {
  timestamp: number;
}
let searchCache: CachedAvailabilities | null = null;
const MAX_DAYS_TO_SEARCH = 7;
export async function searchNextAvailability(
  props: AvailabilitiesForStateProps,
) {
  if (
    searchCache?.availabilityDate === props.date &&
    Date.now() - searchCache.timestamp < TTL
  ) {
    return searchCache;
  }

  searchCache = null;

  const { shouldSearchNextAvailability, setShouldSearchNextAvailability } =
    useApplicationStore.getState();
  let result = await getAvailabilitiesForState(props);

  for (let i = 1; i <= MAX_DAYS_TO_SEARCH; i++) {
    if (result.availabilities.length > 0 || !shouldSearchNextAvailability) {
      break;
    }

    const nextDate = dayjs(props.date).add(i, 'day').format('YYYY-MM-DD');
    result = await getAvailabilitiesForState({ ...props, date: nextDate });
  }

  if (result.availabilities.length === 0 && shouldSearchNextAvailability) {
    // We were unable to find an availability, go back to today's date
    const today = dayjs().format('YYYY-MM-DD');
    result = await getAvailabilitiesForState({ ...props, date: today });
  }

  setShouldSearchNextAvailability(false);

  searchCache = { ...result, timestamp: Date.now() };

  return result;
}

export async function getAvailabilitiesForState({
  state,
  date,
  providerId,
  visitTypeId,
  timeZone,
  trackAvailability,
}: AvailabilitiesForStateProps): Promise<Availabilities> {
  const { fromDateTime, toDateTime } = getAvailabilityRangeFromDate(date!);
  const availabilities = await fetchAvailabilities({
    fromDateTime,
    providerId: providerId || undefined,
    state,
    timeZone,
    toDateTime,
    visitTypeId,
  });

  const { setSoonestAvailabilityDate, soonestAvailabilityDate } =
    useApplicationStore.getState();
  const hasAvailabilities = availabilities.length > 0;
  if (
    (hasAvailabilities && dayjs(fromDateTime).isToday()) ||
    (hasAvailabilities && !soonestAvailabilityDate) ||
    (hasAvailabilities &&
      dayjs(fromDateTime).isBefore(dayjs(soonestAvailabilityDate)))
  ) {
    setSoonestAvailabilityDate(dayjs(fromDateTime).format('YYYY-MM-DD'));
  }
  if (trackAvailability) {
    trackAppointmentTimes(availabilities, 'onboarding', visitTypeId);
  }

  const selectedProvider = providerId
    ? availabilities.find(
        (availability) => availability.provider.id === providerId,
      )?.provider
    : undefined;

  return { availabilities, availabilityDate: date, selectedProvider };
}

export function checkSoonerProviderAvailable(
  soonestTimeSlot: Dayjs,
  availabilities: ProviderAvailability[],
  providerId: string,
) {
  const providerAvailability = availabilities.find(
    (availability) => availability.provider.id === providerId,
  );
  if (providerAvailability) {
    const { date, times } = providerAvailability.timeSlotCollection[0];
    const providerSoonest = getTimeSlotTime(times[0], date);
    return soonestTimeSlot.isBefore(providerSoonest);
  }
  return false;
}
interface AvailabilitiesForPatientProps {
  date?: string | null;
  visitTypeId: string;
  timeZone: string;
  providerId?: string | null;
  trackAvailability: boolean;
}

function getTopTwoLastSeenProviders(availabilities: RebookingAvailabilities) {
  return [
    ...availabilities.availableProviders.filter(
      (p) => p.lastEndedVisitDateTime,
    ),
    ...availabilities.futureAvailableProviders.filter(
      (provider) => provider.lastEndedVisitDateTime,
    ),
  ].slice(0, 2);
}
export async function getAvailabilitiesForPatient({
  date,
  visitTypeId,
  timeZone,
  providerId,
  trackAvailability,
}: AvailabilitiesForPatientProps) {
  const rebookingAvailabilities = await fetchRebookingAvailabilities({
    date,
    timeZone,
    visitTypeId,
  });

  const firstDate =
    rebookingAvailabilities.availableProviders[0]?.timeSlotCollection[0].date;
  const availabilityDate = date || firstDate || dayjs().format('YYYY-MM-DD');

  let isSoonerProviderAvailable = false;
  let soonestTimeSlot;
  const { setSoonestAvailabilityDate, soonestAvailabilityDate } =
    useApplicationStore.getState();
  if ((!date && firstDate) || (firstDate && !soonestAvailabilityDate))
    setSoonestAvailabilityDate(firstDate);
  if (providerId) {
    const soonestAvailabilities = await fetchRebookingAvailabilities({
      timeZone,
      visitTypeId,
    });
    const soonestAvailableProvider =
      soonestAvailabilities.availableProviders[0];
    const { date: soonestDate, times = [] } =
      soonestAvailableProvider?.timeSlotCollection[0] || {};

    if (soonestDate) {
      soonestTimeSlot = getTimeSlotTime(times[0], soonestDate);
      isSoonerProviderAvailable = checkSoonerProviderAvailable(
        soonestTimeSlot,
        rebookingAvailabilities.availableProviders,
        providerId,
      );
    }
  }

  if (trackAvailability) {
    trackAppointmentTimes(
      rebookingAvailabilities.availableProviders,
      'booking',
      visitTypeId,
    );
  }

  const lastSeenProviders = getTopTwoLastSeenProviders(rebookingAvailabilities);

  lastSeenProviders.sort((a, b) => {
    if (dayjs(a.lastEndedVisitDateTime).isBefore(b.lastEndedVisitDateTime))
      return 1;
    return 0;
  });

  const otherProviders = rebookingAvailabilities.availableProviders.filter(
    (availableProviderAvailability) =>
      !lastSeenProviders.some(
        (lastSeenProviderAvailability) =>
          lastSeenProviderAvailability.provider.id ===
          availableProviderAvailability.provider.id,
      ),
  );

  const availabilities = [...lastSeenProviders, ...otherProviders];

  const selectedProvider = providerId
    ? availabilities.find(
        (availability) => availability.provider.id === providerId,
      )?.provider
    : undefined;

  return {
    availabilities,
    selectedProvider,
    availabilityDate,
    isSoonerProviderAvailable,
  };
}

function getTimeData(availabilities: ProviderAvailability[]) {
  const now = dayjs();

  const timeIntervals = [15, 30, 60, 120, 240];

  const slotCounts = new Array(timeIntervals.length + 1).fill(0);
  const providerCounts = new Array(timeIntervals.length + 1).fill(0);

  availabilities.forEach((availability) => {
    availability.timeSlotCollection.forEach((slotCollection) => {
      let providerAdded = false;
      let providerPast240Added = false;
      slotCollection.times.forEach((timeStr) => {
        const slotTime = getTimeSlotTime(timeStr, slotCollection.date);

        for (let i = 0; i < timeIntervals.length; i++) {
          if (slotTime.isBefore(now.add(timeIntervals[i], 'minute'))) {
            slotCounts[i]++;
            if (!providerAdded) {
              providerCounts[i]++;
              providerAdded = true;
            }
            return;
          }
        }

        slotCounts[timeIntervals.length]++;
        if (!providerPast240Added) {
          providerCounts[timeIntervals.length]++;
          providerPast240Added = true;
        }
      });
    });
  });

  return {
    timeSlotsWithin15min: slotCounts[0],
    timeSlotsWithin30min: slotCounts[0] + slotCounts[1],
    timeSlotsWithin60min: slotCounts[0] + slotCounts[1] + slotCounts[2],
    timeSlotsWithin120min:
      slotCounts[0] + slotCounts[1] + slotCounts[2] + slotCounts[3],
    timeSlotsWithin240min:
      slotCounts[0] +
      slotCounts[1] +
      slotCounts[2] +
      slotCounts[3] +
      slotCounts[4],
    timeSlotsPast240min: slotCounts[5],

    providersWithin15min: providerCounts[0],
    providersWithin30min: providerCounts[0] + providerCounts[1],
    providersWithin60min:
      providerCounts[0] + providerCounts[1] + providerCounts[2],
    providersWithin120min:
      providerCounts[0] +
      providerCounts[1] +
      providerCounts[2] +
      providerCounts[3],
    providersWithin240min:
      providerCounts[0] +
      providerCounts[1] +
      providerCounts[2] +
      providerCounts[3] +
      providerCounts[4],
    providersPast240min: providerCounts[5],
  };
}
export function trackAppointmentTimes(
  availabilities: ProviderAvailability[],
  type: string,
  visitTypeId: string,
) {
  const { stateCode, patient } = useApplicationStore.getState();

  const searchState = stateCode || patient.patientProfile?.address.stateCode;
  const { serviceTypeName, visitTypeName } =
    getServiceTypeVisitTypeName(visitTypeId);
  if (availabilities.length) {
    const timeData = getTimeData(availabilities);

    const { date, times } = availabilities[0].timeSlotCollection[0];
    const soonestDateTime = dayjs(`${date} ${times[0]}`, 'YYYY-MM-DD HH:mm');

    const timeUntilFirstAvailability = soonestDateTime.diff(dayjs(), 'minute');
    tracking.trackEvent(Events.APPOINTMENT_TIMES, {
      state: searchState!,
      appointmentsBy: type,
      numberOfProviders: availabilities.length,
      timeToEarliestAvailableAppointment: timeUntilFirstAvailability,
      noAppointmentAvailable: false,
      serviceType: serviceTypeName,
      visitType: visitTypeName,
      hasVisits:
        type === 'onboarding'
          ? false
          : patient.patientProfile?.hasVisits || false,
      earliestAvailableAppointmentWithin15min: timeUntilFirstAvailability <= 15,
      earliestAvailableAppointmentWithin30min: timeUntilFirstAvailability <= 30,
      earliestAvailableAppointmentWithin60min: timeUntilFirstAvailability <= 60,
      earliestAvailableAppointmentWithin120min:
        timeUntilFirstAvailability <= 120,
      earliestAvailableAppointmentWithin240min:
        timeUntilFirstAvailability <= 240,
      earliestAvailableAppointmentPast240min: timeUntilFirstAvailability > 240,
      daysAwayAvailable: soonestDateTime.diff(dayjs(), 'days'),
      dayOfWeekNumber: soonestDateTime.day(),
      dayOfWeekName: soonestDateTime.format('dddd'),
      dayOfWeekNumberName: soonestDateTime.format('d - dddd'),
      timezoneOffset: soonestDateTime.utcOffset() / 60,
      ...timeData,
    });
  } else {
    tracking.trackEvent(Events.APPOINTMENT_TIMES, {
      state: searchState!,
      appointmentsBy: type,
      noAppointmentAvailable: true,
      serviceType: serviceTypeName,
      visitType: visitTypeName,
      hasVisits:
        type === 'onboarding'
          ? false
          : patient.patientProfile?.hasVisits || false,
    });
  }
}

function getServiceTypeVisitTypeName(visitTypeId: string) {
  const { serviceTypes } = useApplicationStore.getState();

  let visitTypeName: string = '';
  let serviceTypeName: string = '';

  serviceTypes.forEach((serviceType) => {
    serviceType.visitTypes.forEach((type) => {
      if (type.visitTypeId === visitTypeId) {
        visitTypeName = type.name;
        serviceTypeName = serviceType.name;
        return;
      }
    });
  });

  return { visitTypeName, serviceTypeName };
}

export async function loadScheduleCachedPromise(
  cachedPromise: AvailabilitiesPromise | null,
  date: string | null,
  providerId: string | null,
) {
  if (cachedPromise) {
    const data: Availabilities | null = await cachedPromise;

    cachedPromise = null;

    if (!data) {
      return;
    }

    if (data.availabilityDate === date) {
      // NOTE: Return early if the requested provider is not found in the cached availabilities to
      const availability = data.availabilities.find(
        ({ provider }) => provider.id === providerId,
      );

      if (!availability) {
        return;
      }

      return data;
    }
  }
}

export enum VisitTypeIds {
  AddictionTreatment = '972',
  MenopauseHormoneTherapy = 'mhtpayg-15c9-4eaa-a21f-b7264560ef35',
  WeightLoss = '11987721',
}
